import { Component, DestroyRef, inject, Injector, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatNativeDateModule,
} from '@angular/material/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { RxIconModule } from '@nrwl/rx-icon';
import { TokenToDivPipe } from '../../../helpers/token-to-div.pipe';
import { WidgetBase } from '@org/rx-form-builder';
import { createLocalDate, parseLocalDateString } from '../../../helpers/date-utils';
import { WidgetApiService } from '../../../rx-form-presenter/services/widget-api.service';
import { catchError, debounceTime, map } from 'rxjs/operators';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import {
  VITALS_AUTO_CALCULATION_HINT,
  VITALS_DATE_FORMATS,
  VITALS_ERROR_TEXTS,
} from './fb-emr-vitals.constants';
import { DateInputGuardDirective } from '../../../helpers/directives';
import { FITZPATRICK_OPTIONS } from './fb-emr-vitals.types';
import { PneTreatmentVitals } from '../../../rx-form-presenter/types/pne.types';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { DecimalDisplayDirective } from '../../../helpers/directives';
import { FbEmrVitalsWidget, VitalsMetadata } from '../../../models/form-widget-emr-vitals.model';
import { isEmpty } from 'lodash';
import { NormalizeNumericDirective } from './directives/normalize-numeric.directive';
import { normalizeNumericValue } from './helpers/normalize-numeric.util';
import {
  calcBmiImperial,
  calcLastVisitDiff,
  calcStartDiff,
} from './helpers/vitals-calculations.util';
import { EmrReadonlyNotice } from '../../../helpers/components/emr-readonly-notice';
import { TextareaAutosizeDirective } from '../shared/textarea-autosize-directive';
import { PasteStripDirective } from '../shared/paste-strip-directive';
import { ScribeSuggestionIndicatorComponent } from '../shared/scribe-suggestion-indicator/scribe-suggestion-indicator.component';
import { AmendmentFieldIndicatorComponent } from '../shared/amendment-field-indicator/amendment-field-indicator.component';
import { ScribeSuggestionMarkerTarget } from '../../scribe-suggestion-marker.model';
import { copyScribeSuggestionMarker } from '../../scribe-suggestion-marker.util';

interface VitalsForm {
  id: FormControl<string | null>;
  treatment_id: FormControl<string | null>;
  start_weight: FormControl<number | null>;
  height_ft: FormControl<number | null>;
  height_in: FormControl<number | null>;
  weight: FormControl<number | null>;
  goal_weight: FormControl<number | null>;
  body_fat_percentage: FormControl<number | null>;
  prev_weight: FormControl<number | null>;
  blood_pressure: FormControl<string | null>;
  bmi: FormControl<number | null>;
  pulse: FormControl<number | null>;
  bmr: FormControl<number | null>;
  respiration: FormControl<number | null>;
  tdee: FormControl<number | null>;
  fitzpatrick_scale: FormControl<string | null>;
  vital_notes: FormControl<string | null>;
  waist: FormControl<number | null>;
  hip: FormControl<number | null>;
  chest_bust: FormControl<number | null>;
  left_arm: FormControl<number | null>;
  right_arm: FormControl<number | null>;
  left_thigh: FormControl<number | null>;
  right_thigh: FormControl<number | null>;
  neck: FormControl<number | null>;
  vtc: FormControl<number | null>;
  last_menstrual: FormControl<Date | string | null>;
  start_diff: FormControl<number | null>;
  last_visit_diff: FormControl<number | null>;
}

const VITALS_SCRIBE_MARKER_GROUPS: Readonly<Record<string, readonly string[]>> = {
  height_ft: ['height_ft', 'height_in'],
  height_in: ['height_ft', 'height_in'],
};

@Component({
  selector: 'fb-emr-vitals',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDividerModule,
    MatDatepickerModule,
    MatNativeDateModule,
    NgSelectModule,
    RxIconModule,
    TokenToDivPipe,
    DateInputGuardDirective,
    MatProgressSpinner,
    DecimalDisplayDirective,
    NormalizeNumericDirective,
    EmrReadonlyNotice,
    TextareaAutosizeDirective,
    PasteStripDirective,
    ScribeSuggestionIndicatorComponent,
    AmendmentFieldIndicatorComponent,
  ],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: VITALS_DATE_FORMATS },
  ],
  templateUrl: './fb-emr-vitals.component.html',
  styleUrls: ['./fb-emr-vitals.component.scss'],
})
export class FbEmrVitalsComponent extends WidgetBase implements OnInit {
  @Input()
  public override set payload(value) {
    this._payload = value;
  }
  public override get payload() {
    return this._payload;
  }

  @Input()
  public override set patientId(value) {
    this._patientId = value;
  }
  public override get patientId() {
    return this._patientId;
  }

  @Input()
  public override set isEmrReadonly(value: boolean) {
    super.isEmrReadonly = value;
    this.applyReadOnlyFieldsAndHints();
  }
  public override get isEmrReadonly(): boolean {
    return super.isEmrReadonly;
  }

  @Input()
  public override set entity(value) {
    this._entity = value;
    this.formTemplateId = this._entity?.template?.id ?? this._entity?.id;
  }
  public override get entity() {
    return this._entity;
  }

  @Input()
  public set treatmentId(value: string | number | null) {
    this._treatmentId = value;
  }
  public get treatmentId(): string | number | null {
    return this._treatmentId;
  }

  @Input()
  public override set patient(value: any) {
    super.patient = value;
  }
  public override get patient() {
    return super.patient;
  }

  @Input()
  public override set appointment(value: any) {
    super.appointment = value;
  }
  public override get appointment() {
    return super.appointment;
  }

  public formTemplateId: string;
  public form: FormGroup<VitalsForm>;
  public readonly errorTexts = VITALS_ERROR_TEXTS;
  public readonly fitzpatrickOptions = FITZPATRICK_OPTIONS;
  public readonly autoCalculationHint = VITALS_AUTO_CALCULATION_HINT;
  private isFormLoaded = new BehaviorSubject<boolean>(false);
  private showAutoCalcHint = new BehaviorSubject<boolean>(false);
  public readonly isFormLoaded$ = this.isFormLoaded.asObservable();
  public readonly showAutoCalcHint$ = this.showAutoCalcHint.asObservable();
  private _treatmentId: string | number | null = null;
  private vitalsLoading = false;
  private vitalsLoaded = false;
  private lastTreatmentId: string | null = null;

  private readonly widgetApiService = inject(WidgetApiService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  constructor(injector: Injector) {
    super(injector);

    this.form = this.initForm();
  }

  /**
   * True while a patient's own plan is being customized. The widget then stays an empty,
   * disabled form and never reads or writes the saved vitals, so they are stored back unchanged.
   */
  public get isInstanceDesign(): boolean {
    return !!this.isDesign && !!this.entity;
  }

  public ngOnInit(): void {
    this.form.valueChanges
      .pipe(debounceTime(500), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateCalculatedFields();
        this.updatePayload();
      });

    this.loadAndInitFormData();
  }

  public applySuggestionSet(patch: Partial<VitalsMetadata> | Record<string, unknown>): void {
    if (this.isInstanceDesign || !patch || typeof patch !== 'object') {
      return;
    }

    const normalizedPatch: Record<string, unknown> = {};

    Object.entries(patch).forEach(([key, rawValue]) => {
      if (!(key in this.form.controls)) {
        return;
      }

      normalizedPatch[key] = this.normalizeSuggestionValueForControl(key, rawValue);
    });

    if (!Object.keys(normalizedPatch).length) {
      return;
    }

    this.form.patchValue(normalizedPatch, { emitEvent: false });
    this.updateCalculatedFields();
    this.updatePayload();
  }

  public override isValid(): boolean {
    return this.form ? this.form.valid : true;
  }

  public getMetadata(): Record<string, unknown> {
    if (this.payload && this.payload.properties) {
      const widgetData = { ...this.payload };
      widgetData.properties = { ...widgetData.properties };

      const widget = new FbEmrVitalsWidget(widgetData);

      return widget.getMetadata();
    }

    return { vitals: {} };
  }

  public getValue() {
    return this.form.getRawValue();
  }

  public controlId(fieldName: string): string {
    return `vitals-${String(fieldName)}`;
  }

  public isFieldVisible(fieldName: string): boolean {
    const items = this.payload?.properties?.items?.value;

    if (!items || !Array.isArray(items)) {
      return true;
    }

    const category = items.find((cat: any) => cat.nested && cat.items);
    if (!category || !Array.isArray(category.items)) {
      return true;
    }

    const field = category.items.find((item: any) => item.name === fieldName);

    if (!field) {
      return false;
    }

    return field.visible !== false;
  }

  public get vitalsMarkerTarget(): ScribeSuggestionMarkerTarget | null {
    const target = this.payload?.properties?.value ?? this.payload?.properties?.items?.value;
    return target && typeof target === 'object' && !Array.isArray(target) ? target : null;
  }

  public clearVitalsMarker(fieldKey: string): void {
    const markerKeys = VITALS_SCRIBE_MARKER_GROUPS[fieldKey] ?? [fieldKey];
    markerKeys.forEach((markerKey) =>
      this.clearScribeSuggestionMarker(this.vitalsMarkerTarget, markerKey),
    );
  }

  private updateCalculatedFields(): void {
    const data = this.form.getRawValue() as VitalsMetadata;

    this.form.patchValue(
      {
        start_diff: calcStartDiff(data),
        last_visit_diff: calcLastVisitDiff(data),
        bmi: calcBmiImperial(data),
      },
      { emitEvent: false },
    );
  }

  private loadFormData(treatmentId: string | null): Observable<VitalsMetadata | null> {
    if (this.isPrinting || this.isEmrReadonly) {
      return of(this.resolveLocalVitalsData());
    }

    if (this.isPreview && !this.formTemplateId) {
      return of(null);
    }

    const vitalsMetadata = this.entity?.metadata?.vitals;
    if (this.formTemplateId && this.hasMeaningfulVitalsMetadata(vitalsMetadata)) {
      return of(vitalsMetadata);
    }

    if (!treatmentId) {
      return of(null);
    }
    const params = {
      TreatmentId: treatmentId,
    };

    return this.widgetApiService.getCustomerVitals(params).pipe(
      map((res) => {
        const rawData = res.find(({ TreatmentId }) => TreatmentId === params.TreatmentId);
        return rawData ? this.mapPneToForm(rawData) : null;
      }),
      catchError(() => of(null)),
    );
  }

  private resolveLocalVitalsData(): VitalsMetadata | null {
    const payloadValue = this.payload?.properties?.value;
    if (payloadValue && !isEmpty(payloadValue)) {
      return payloadValue as VitalsMetadata;
    }

    const metadataValue = this.entity?.metadata?.vitals;
    if (metadataValue && !isEmpty(metadataValue)) {
      return metadataValue as VitalsMetadata;
    }

    return null;
  }

  private hasMeaningfulVitalsMetadata(
    vitalsMetadata: VitalsMetadata | null | undefined,
  ): vitalsMetadata is VitalsMetadata {
    if (!vitalsMetadata || isEmpty(vitalsMetadata)) {
      return false;
    }

    return Object.values(vitalsMetadata).some((value) => this.hasAnyValue(value));
  }

  private loadAndInitFormData(): void {
    this.tryLoadVitals();
  }

  private tryLoadVitals(): void {
    if (this.vitalsLoading || this.vitalsLoaded) {
      return;
    }

    if (this.isInstanceDesign) {
      this.form.disable({ emitEvent: false });
      this.isFormLoaded.next(true);
      this.ready.next(true);
      return;
    }

    this.ready.next(false);
    this.isFormLoaded.next(false);

    const patient = this.patient;
    if (!patient && !this.isPrinting) {
      this.isFormLoaded.next(true);
      this.ready.next(true);
      return;
    }

    const appointmentTreatmentId = this.appointment?.TreatmentId ?? this.treatmentId ?? null;
    const metadataTreatmentId = this.entity?.metadata?.treatment_id ?? null;
    const inputTreatmentId =
      this._treatmentId !== null && this._treatmentId !== undefined && this._treatmentId !== ''
        ? String(this._treatmentId)
        : null;
    const treatmentId = inputTreatmentId ?? appointmentTreatmentId ?? metadataTreatmentId ?? null;

    if (treatmentId && treatmentId !== this.lastTreatmentId) {
      this.vitalsLoaded = false;
      this.lastTreatmentId = treatmentId;
    }

    this.vitalsLoading = true;
    this.loadFormData(treatmentId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.vitalsLoading = false;
          this.vitalsLoaded = true;
          if (data) {
            this.form.patchValue(data, { emitEvent: false });

            this.form.patchValue(
              {
                start_diff: calcStartDiff(data),
                last_visit_diff: calcLastVisitDiff(data),
                bmi: calcBmiImperial(data),
              },
              { emitEvent: false },
            );

            this.updatePayload();
          }
          this.applyReadOnlyFieldsAndHints();
          this.isFormLoaded.next(true);
          this.ready.next(true);
        },
        error: () => {
          this.vitalsLoading = false;
          this.isFormLoaded.next(true);
          this.ready.next(true);
        },
      });
  }

  private applyReadOnlyFieldsAndHints(): void {
    if (this.isInstanceDesign) {
      this.form.disable({ emitEvent: false });
      this.showAutoCalcHint.next(false);
      return;
    }

    if (this.isEmrReadonly) {
      this.form.disable({ emitEvent: false });
      this.showAutoCalcHint.next(false);

      if (this.payload?.properties) {
        this.payload.properties.formInvalid = false;
      }

      return;
    }

    if (this.form.disabled) {
      this.form.enable({ emitEvent: false });
    }

    if (this.isPreview || this.isPrinting || isEmpty(this.patient)) {
      return;
    }

    // Read-only fields
    this.form.controls?.['prev_weight']?.disable({ emitEvent: false });
    this.form.controls?.['bmi']?.disable({ emitEvent: false });
    this.form.controls?.['start_diff']?.disable({ emitEvent: false });
    this.form.controls?.['last_visit_diff']?.disable({ emitEvent: false });

    this.showAutoCalcHint.next(true);
  }

  private updatePayload(): void {
    if (this.isInstanceDesign) {
      return;
    }

    if (this.payload?.properties) {
      const properties = this.payload.properties;
      const currentValue = properties.value;
      const nextValue = {
        ...this.getValue(),
      };

      copyScribeSuggestionMarker(currentValue, nextValue);
      properties.value = nextValue;

      properties.formInvalid = this.form.invalid;
    }
  }

  private initForm(): FormGroup<VitalsForm> {
    return this.fb.group({
      id: this.fb.control<string | null>(null),
      treatment_id: this.fb.control<string | null>(null),
      start_weight: this.fb.control<number | null>(null),
      height_ft: this.fb.control<number | null>(null, [Validators.min(0), Validators.max(1000)]),
      height_in: this.fb.control<number | null>(null, [Validators.min(0), Validators.max(1000)]),
      weight: this.fb.control<number | null>(null, [Validators.min(0), Validators.max(1000)]),
      goal_weight: this.fb.control<number | null>(null, [Validators.min(0), Validators.max(1000)]),
      body_fat_percentage: this.fb.control<number | null>(null, [
        Validators.min(0),
        Validators.max(1000),
      ]),
      pulse: this.fb.control<number | null>(null, [Validators.min(0), Validators.max(1000)]),
      bmr: this.fb.control<number | null>(null, [Validators.min(0)]),
      respiration: this.fb.control<number | null>(null, [Validators.min(0), Validators.max(1000)]),
      tdee: this.fb.control<number | null>(null, [Validators.min(0)]),
      waist: this.fb.control<number | null>(null, [Validators.min(0), Validators.max(1000)]),
      hip: this.fb.control<number | null>(null, [Validators.min(0), Validators.max(1000)]),
      chest_bust: this.fb.control<number | null>(null, [Validators.min(0), Validators.max(1000)]),
      left_arm: this.fb.control<number | null>(null, [Validators.min(0), Validators.max(1000)]),
      right_arm: this.fb.control<number | null>(null, [Validators.min(0), Validators.max(1000)]),
      left_thigh: this.fb.control<number | null>(null, [Validators.min(0), Validators.max(1000)]),
      right_thigh: this.fb.control<number | null>(null, [Validators.min(0), Validators.max(1000)]),
      neck: this.fb.control<number | null>(null, [Validators.min(0), Validators.max(1000)]),
      vtc: this.fb.control<number | null>(null, [Validators.min(0), Validators.max(1000)]),
      last_menstrual: this.fb.control<Date | string | null>(null),
      fitzpatrick_scale: this.fb.control<string | null>(null),
      vital_notes: this.fb.control<string | null>(null),
      blood_pressure: this.fb.control<string | null>(null),
      // Read-only fields
      prev_weight: this.fb.control<number | null>(null),
      bmi: this.fb.control<number | null>(null),
      start_diff: this.fb.control<number | null>(null),
      last_visit_diff: this.fb.control<number | null>(null),
    });
  }

  private mapPneToForm(data: PneTreatmentVitals): VitalsMetadata {
    return {
      id: data.Id,
      treatment_id: data.TreatmentId,
      start_weight: data.StartingWeight,
      height_ft: data.HeightFeet,
      height_in: data.HeightInches,
      weight: data.WeightLbs,
      goal_weight: data.GoalWeightLbs,
      body_fat_percentage: data.BodyFatPercent,
      blood_pressure: data.BloodPressure,
      pulse: normalizeNumericValue(data.Pulse),
      bmr: normalizeNumericValue(data.BMR),
      tdee: normalizeNumericValue(data.TDEE),
      respiration: normalizeNumericValue(data.RespRate),
      last_menstrual: this.parseDate(data.LastMenstrual),
      fitzpatrick_scale: data.FitzpatrickScale,
      vital_notes: data.VitalNotes,
      waist: data.WaistInches,
      hip: data.HipImperial,
      chest_bust: data.ChestInches,
      left_arm: data.LeftArmInches,
      right_arm: data.RightArmInches,
      left_thigh: data.LeftThighInches,
      right_thigh: data.RightThighInches,
      neck: data.NeckInches,
      vtc: data.VTC,
      prev_weight: data.PreviousWeight,
    };
  }

  private parseDate(value: Date): string | null {
    if (!value) {
      return null;
    }

    const parsed = parseLocalDateString(value);
    if (!parsed?.trim().length) {
      return null;
    }

    // Backend sometimes sends "1/1/1901"; treat it as invalid.
    if (parsed.startsWith('1901-')) {
      return null;
    }

    return parsed;
  }

  private hasAnyValue(value: unknown): boolean {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'number') {
      return !Number.isNaN(value);
    }

    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    if (value instanceof Date) {
      return !Number.isNaN(value.getTime());
    }

    return true;
  }

  private normalizeSuggestionValueForControl(key: string, rawValue: unknown): unknown {
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return null;
    }

    if (key === 'fitzpatrick_scale') {
      const normalized = String(rawValue).replace(/\s+/g, ' ').trim().toLowerCase();
      const matched = this.fitzpatrickOptions.find(
        (option) =>
          String(option.value).replace(/\s+/g, ' ').trim().toLowerCase() === normalized ||
          String(option.text).replace(/\s+/g, ' ').trim().toLowerCase() === normalized,
      );

      return matched?.value ?? String(rawValue);
    }

    if (key === 'last_menstrual') {
      if (rawValue instanceof Date) {
        return Number.isNaN(rawValue.getTime()) ? null : rawValue;
      }

      const parsed = createLocalDate(parseLocalDateString(String(rawValue)));
      return parsed ?? null;
    }

    if (this.isNumericVitalsControl(key)) {
      const normalized = typeof rawValue === 'number' ? rawValue : Number(String(rawValue).trim());
      return Number.isFinite(normalized) ? normalized : null;
    }

    return String(rawValue);
  }

  private isNumericVitalsControl(key: string): boolean {
    return new Set([
      'height_ft',
      'height_in',
      'weight',
      'goal_weight',
      'body_fat_percentage',
      'prev_weight',
      'pulse',
      'bmr',
      'respiration',
      'tdee',
      'waist',
      'hip',
      'chest_bust',
      'left_arm',
      'right_arm',
      'left_thigh',
      'right_thigh',
      'neck',
      'vtc',
      'start_weight',
      'bmi',
      'start_diff',
      'last_visit_diff',
    ]).has(key);
  }
}
